import { useEffect, useRef, useState } from 'react';
import { CheckCircle2, Play } from 'lucide-react';
import { Button } from '../../button/button';
import { Input } from '../../input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../select';
import { Switch } from '../../switch';
import { cn } from '../../../../lib/utils';
import { runConnectionCheck } from '../../../../lib/managed-agents/api';
import type {
  ConnectionCheckResult,
  RemoteEnvironment,
  RemoteManagedAgent,
  ToolPolicy,
} from '../../../../lib/managed-agents/types';
import { INSET_CLASS } from '../constants';
import { FieldLabel } from '../form-parts';
import { StepTitle } from '../wizard-frame';

interface ChooseAgentStepProps {
  agents: RemoteManagedAgent[];
  importedVersions: Map<string, number>;
  selectedId: string;
  onSelect: (id: string) => void;
}

export function ChooseAgentStep({
  agents,
  importedVersions,
  selectedId,
  onSelect,
}: ChooseAgentStepProps) {
  const available = agents.filter((agent) => !agent.archivedAt);

  return (
    <>
      <StepTitle>Which agent do you want to test?</StepTitle>
      {available.length === 0 ? (
        <p className="text-sm text-prompt-safecomabbey">
          No agents yet. Create one in Claude Console, then come back.
        </p>
      ) : (
        <div
          role="radiogroup"
          aria-label="Agent"
          className="flex flex-col gap-2"
        >
          {available.map((agent) => {
            const importedVersion = importedVersions.get(agent.id);
            return (
              <label
                key={agent.id}
                className={cn(
                  'flex cursor-pointer items-center gap-3 rounded-lg border bg-prompt-safecomwhite px-3 py-2.5',
                  selectedId === agent.id
                    ? 'ring-prompt-safecomoutrageous-orange/15 border-prompt-safecomoutrageous-orange ring-2'
                    : 'border-[#CED4DA]',
                )}
              >
                <input
                  type="radio"
                  name="managed-agent"
                  value={agent.id}
                  checked={selectedId === agent.id}
                  onChange={() => onSelect(agent.id)}
                  className="accent-prompt-safecomoutrageous-orange"
                />
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="text-sm font-semibold text-prompt-safecomvulcan">
                    {agent.name}
                  </span>
                  {agent.description && (
                    <span className="truncate text-xs text-prompt-safecomabbey">
                      {agent.description}
                    </span>
                  )}
                </span>
                <span className="flex-none text-xs text-prompt-safecomabbey">
                  {importedVersion
                    ? `Imported · v${importedVersion}`
                    : `v${agent.version}`}
                </span>
              </label>
            );
          })}
        </div>
      )}
    </>
  );
}

export interface ImportSettings {
  environmentId: string;
  budget: string;
  greeting: string;
  toolPolicy: ToolPolicy;
}

interface SettingsStepProps {
  environments: RemoteEnvironment[];
  settings: ImportSettings;
  onChange: (settings: ImportSettings) => void;
  budgetError: string | null;
}

export function SettingsStep({
  environments,
  settings,
  onChange,
  budgetError,
}: SettingsStepProps) {
  const set = <K extends keyof ImportSettings>(
    key: K,
    value: ImportSettings[K],
  ) => onChange({ ...settings, [key]: value });

  return (
    <>
      <StepTitle>A few settings</StepTitle>
      <div className="grid gap-5 md:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <FieldLabel help="Where the agent's tools run. A test environment keeps simulations away from real data.">
            Environment
          </FieldLabel>
          {environments.length ? (
            <Select
              value={settings.environmentId}
              onValueChange={(value) => set('environmentId', value)}
            >
              <SelectTrigger
                aria-label="Environment"
                className="bg-prompt-safecomwhite"
              >
                <SelectValue placeholder="Select an environment" />
              </SelectTrigger>
              <SelectContent>
                {environments.map((env) => (
                  <SelectItem key={env.id} value={env.id}>
                    {env.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <p className="text-sm text-red-700">
              No environments yet. Create one in Claude Console first.
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <FieldLabel
            htmlFor="import-budget"
            help="Your Anthropic account pays for the agent. Anthropic stops it when one simulation reaches this amount."
          >
            Spend limit per simulation
          </FieldLabel>
          <div className="flex items-center gap-2">
            <span className="text-sm text-prompt-safecomabbey">$</span>
            <Input
              id="import-budget"
              type="number"
              min="0.01"
              max="1000"
              step="0.01"
              value={settings.budget}
              onChange={(event) => set('budget', event.target.value)}
              aria-invalid={Boolean(budgetError)}
              className="max-w-[140px] bg-prompt-safecomwhite tabular-nums"
            />
          </div>
          {budgetError && <p className="text-xs text-red-700">{budgetError}</p>}
        </div>

        <div className="flex flex-col gap-1.5 md:col-span-2">
          <FieldLabel
            htmlFor="import-greeting"
            help="The first line of every simulation, like the first bubble in your chat widget. Leave it blank if your product doesn't show one."
          >
            Opening message
          </FieldLabel>
          <Input
            id="import-greeting"
            value={settings.greeting}
            maxLength={500}
            onChange={(event) => set('greeting', event.target.value)}
            placeholder="Optional: your agent's opening message"
            className="bg-prompt-safecomwhite"
          />
          <p className="text-xs text-prompt-safecomabbey">
            Optional. Your agent never sends it, but the persona replies as if
            it did. Leave it blank and the persona starts the conversation.
          </p>
        </div>

        <div className="flex items-center gap-3 md:col-span-2">
          <Switch
            id="import-tools"
            checked={settings.toolPolicy === 'allow'}
            onCheckedChange={(checked) =>
              set('toolPolicy', checked ? 'allow' : 'deny')
            }
          />
          <FieldLabel
            htmlFor="import-tools"
            help="Off: the agent is told tools are unavailable and answers without them. Turn on only for agents in a test environment, since tools may send emails, write files or call your systems."
          >
            Allow tools that need approval
          </FieldLabel>
        </div>
      </div>
    </>
  );
}

interface CheckStepProps {
  workspaceId: string;
  anthropicAgentId: string;
  environmentId: string;
  /** The imported agent, marked as working when it replies. */
  agentId: string;
}

export function CheckStep({
  workspaceId,
  anthropicAgentId,
  environmentId,
  agentId,
}: CheckStepProps) {
  const [result, setResult] = useState<ConnectionCheckResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => () => abortRef.current?.abort(), []);

  const run = async () => {
    abortRef.current = new AbortController();
    setIsRunning(true);
    setError(null);
    setResult(null);
    try {
      setResult(
        await runConnectionCheck({
          workspaceId,
          anthropicAgentId,
          environmentId,
          agentId,
          signal: abortRef.current.signal,
        }),
      );
    } catch (err) {
      if (!abortRef.current.signal.aborted) {
        setError(err instanceof Error ? err.message : 'The test failed.');
      }
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <>
      <div className="flex flex-col gap-1">
        <StepTitle>Say hello to your agent</StepTitle>
        <p className="text-sm text-prompt-safecomabbey">
          One test message. Costs a few cents.
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="outline" onClick={run} disabled={isRunning}>
          <Play className="h-4 w-4" />
          {isRunning
            ? 'Waiting for a reply…'
            : result || error
              ? 'Try again'
              : 'Say hello'}
        </Button>
      </div>
      {error && (
        <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-800">
          {error}
        </p>
      )}
      {result && (
        <div className={cn('flex flex-col gap-2 p-4 text-sm', INSET_CLASS)}>
          <p className="flex items-center gap-2 font-medium text-green-800">
            <CheckCircle2 className="h-4 w-4 flex-none" />
            It works
            <span className="ml-auto text-xs font-normal tabular-nums text-prompt-safecomabbey">
              {result.seconds} s
            </span>
          </p>
          <p className="whitespace-pre-wrap text-prompt-safecomvulcan">
            {result.reply}
          </p>
        </div>
      )}
    </>
  );
}
